import type { UploadFileRequest } from '../../api';
import type { MediaKind } from '../../constants/assetTypes';

/**
 * Client-side upload limits, mirrored from the backend.
 *
 * Duplicated deliberately rather than fetched: these gate the file picker
 * before a batch is ever opened, so waiting on a round trip to tell the user
 * their PDF isn't an image would be slower and no more correct. The backend
 * still enforces all of it — this only saves the trip.
 *
 * Mirrors `apps/asset-library/backend/src/validation/media.py` and the
 * `max_length` constraints in `src/api/schemas.py`. If you change one, change
 * the other.
 */

/** Extension-per-MIME, matching the backend's `ALLOWED_IMAGE_TYPES`. */
export const ALLOWED_IMAGE_TYPES: Record<string, string> = {
  'image/jpeg': '.jpg',
  'image/jpg': '.jpg',
  'image/png': '.png',
  'image/gif': '.gif',
  'image/webp': '.webp',
  'image/svg+xml': '.svg',
};

/**
 * What a carousel frame may be: `CAROUSEL_FRAME_TYPES` in the backend's
 * `validation/media.py`. Raster only — every frame must be the same pixel size
 * and an SVG has none — and no GIF, because a carousel card is a still.
 */
export const CAROUSEL_FRAME_TYPES: Record<string, string> = {
  'image/jpeg': '.jpg',
  'image/jpg': '.jpg',
  'image/png': '.png',
  'image/webp': '.webp',
};

export const ALLOWED_VIDEO_TYPES: Record<string, string> = {
  'video/mp4': '.mp4',
  'video/quicktime': '.mov',
  'video/webm': '.webm',
  'video/x-m4v': '.m4v',
};

export const ALLOWED_MEDIA_TYPES: Record<string, string> = {
  ...ALLOWED_IMAGE_TYPES,
  ...ALLOWED_VIDEO_TYPES,
};

/** `MAX_FILE_SIZE` in `validation/media.py`. */
export const MAX_FILE_SIZE = 1024 * 1024 * 1024; // 1 GiB

/** `CreateBatchRequest.files` is `max_length=500`. */
export const MAX_BATCH_FILES = 500;

/** `UploadFileRequest.filename` is `max_length=500`. */
export const MAX_FILENAME_LENGTH = 500;

/** `CreateBatchRequest.label` is `max_length=200`. */
export const MAX_LABEL_LENGTH = 200;

/**
 * Extension → MIME, for when the browser's own guess is unusable.
 *
 * Not derived from `ALLOWED_MEDIA_TYPES` by inversion: that map is one
 * extension per MIME, and the cases that need this are the extra spellings it
 * has no room for — `.jpeg` alongside `.jpg`.
 */
const EXTENSION_TO_TYPE: Record<string, string> = {
  '.jpg': 'image/jpeg',
  '.jpeg': 'image/jpeg',
  '.png': 'image/png',
  '.gif': 'image/gif',
  '.webp': 'image/webp',
  '.svg': 'image/svg+xml',
  '.mp4': 'video/mp4',
  '.mov': 'video/quicktime',
  '.webm': 'video/webm',
  '.m4v': 'video/x-m4v',
};

/**
 * The MIME types one kind of upload accepts: stills for source and master
 * images, films for filmic. OLA is the folder route and has no file picker.
 */
export function allowedTypesFor(mediaKind: MediaKind): Record<string, string> {
  if (mediaKind === 'video') return ALLOWED_VIDEO_TYPES;
  if (mediaKind === 'image') return ALLOWED_IMAGE_TYPES;
  if (mediaKind === 'carousel') return CAROUSEL_FRAME_TYPES;
  return {};
}

/** The `accept` attribute for the file input, derived from the same source. */
export function fileInputAccept(allowed: Record<string, string> = ALLOWED_MEDIA_TYPES): string {
  return [
    ...Object.keys(allowed),
    // Extensions as well as MIME types: the picker matches on either, and a
    // file the OS reports as `application/octet-stream` is only reachable via
    // the extension.
    ...Object.entries(EXTENSION_TO_TYPE)
      .filter(([, type]) => type in allowed)
      .map(([extension]) => extension),
  ].join(',');
}

export interface AcceptedFile {
  file: File;
  /** Sent as-is to `POST /batches`; the signed URL is bound to this exact value. */
  content_type: string;
}

export interface RejectedFile {
  filename: string;
  /** Shown next to the file in the modal, so the user knows which one and why. */
  reason: string;
  /** Refused only for the per-upload file cap, not for anything about the file itself. */
  overLimit?: boolean;
}

export interface ValidationResult {
  accepted: AcceptedFile[];
  rejected: RejectedFile[];
}

/** One decimal, rounded up, so a file just over a limit never reads as equal to it. */
export function formatSize(bytes: number): string {
  if (bytes >= 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024 * 1024)).toFixed(1)} GB`;
  return `${Math.ceil((bytes / (1024 * 1024)) * 10) / 10} MB`;
}

/**
 * The browser reports the MIME type, but not always, and not always usefully.
 *
 * A file dragged from an unusual source arrives with `type: ''`; one copied
 * from a network share or an older Windows profile can arrive as
 * `application/octet-stream` or `image/x-png`. Both are real PNGs the backend
 * would accept, so any type outside the allow-list falls through to the
 * extension rather than being rejected on the browser's word. The backend
 * re-checks the magic number at confirm time, so guessing here can only ever
 * be a convenience, never a hole.
 */
export function resolveContentType(file: File, allowed: Record<string, string>): string | null {
  if (file.type && file.type in allowed) return file.type;

  const dot = file.name.lastIndexOf('.');
  if (dot === -1) return null;

  const guessed = EXTENSION_TO_TYPE[file.name.slice(dot).toLowerCase()];
  return guessed && guessed in allowed ? guessed : null;
}

/**
 * Which media kinds a type map covers. One test, shared by the rejection
 * reason and the dropzone's caption, so the two never disagree about what a
 * map of types means in words.
 */
export function mediaKindsIn(allowed: Record<string, string>): {
  images: boolean;
  videos: boolean;
} {
  const types = Object.keys(allowed);
  return {
    images: types.some((type) => type in ALLOWED_IMAGE_TYPES),
    videos: types.some((type) => type in ALLOWED_VIDEO_TYPES),
  };
}

/** What the rejection says the upload takes, in the user's words. */
function describeAllowed(allowed: Record<string, string>): string {
  const { images, videos } = mediaKindsIn(allowed);
  if (images && videos) return 'image or video';
  return videos ? 'video' : 'image';
}

/**
 * Split a file list into what can be uploaded and what can't.
 *
 * Returns rejections per file rather than throwing on the first bad one: a user
 * who drags a folder of 40 images and 2 PDFs wants the 40 uploaded and to be
 * told about the 2, not to start over.
 *
 * `alreadyQueued` is the count already staged in the modal, so dropping a
 * second batch over the top respects the same 500 cap the backend applies to
 * the whole request.
 *
 * `allowed` narrows the accepted types to the kind being uploaded: a video
 * dropped into a batch of master images is refused here, where the user can
 * see which file and why, rather than by the backend's 400 for the whole batch.
 */
export function validateFiles(
  files: File[],
  alreadyQueued = 0,
  allowed: Record<string, string> = ALLOWED_MEDIA_TYPES,
): ValidationResult {
  const accepted: AcceptedFile[] = [];
  const rejected: RejectedFile[] = [];
  const kind = describeAllowed(allowed);

  for (const file of files) {
    if (accepted.length + alreadyQueued >= MAX_BATCH_FILES) {
      rejected.push({
        filename: file.name,
        reason: `Over the ${MAX_BATCH_FILES}-file limit for one upload`,
        overLimit: true,
      });
      continue;
    }

    const contentType = resolveContentType(file, allowed);
    if (contentType === null) {
      rejected.push({
        filename: file.name,
        reason: file.type
          ? `${file.type} isn’t a supported ${kind} type`
          : `Not a supported ${kind}`,
      });
      continue;
    }

    if (file.size > MAX_FILE_SIZE) {
      rejected.push({
        filename: file.name,
        reason: `${formatSize(file.size)} is over the ${formatSize(MAX_FILE_SIZE)} limit`,
      });
      continue;
    }

    // Zero bytes passes every check above and then fails server-side at
    // confirm, after the user has watched a progress bar fill for nothing.
    if (file.size === 0) {
      rejected.push({ filename: file.name, reason: 'File is empty' });
      continue;
    }

    if (file.name.length > MAX_FILENAME_LENGTH) {
      rejected.push({
        filename: file.name,
        reason: `Filename is over ${MAX_FILENAME_LENGTH} characters`,
      });
      continue;
    }

    accepted.push({ file, content_type: contentType });
  }

  return { accepted, rejected };
}

/** The `files` payload for `POST /batches`, in the same order as the accepted list. */
export function toUploadRequests(accepted: AcceptedFile[]): UploadFileRequest[] {
  return accepted.map(({ file, content_type }) => ({ filename: file.name, content_type }));
}
